import { IUser } from '~/src/dash/api/console-api';
import { IPermission, IPermissionsTemplate } from '~/src/dash/api/permissions/model';
import { warn } from '~/src/dash/logging/logging';
import { hasRole, ROLE_SUPER } from '~/src/dash/utils/roles';

export interface IExtendedPermission extends IPermission {
  fullPath: string;
}

export interface IPermissionCheck {
  isAuthorized: boolean;
  missingPermissions: IExtendedPermission[];
}

function isNullOrUndefined(v: any): boolean {
  return v === undefined || v === null;
}

const emptyIextendedPermission = {
  name: '',
  fullPath: '',
  permissions: [],
  description: '',
} as IExtendedPermission;

export const allowedEmptyIPermissionCheck = {
  isAuthorized: true,
  missingPermissions: [],
} as IPermissionCheck;

export const notAllowedEmptyIPermissionCheck = {
  isAuthorized: false,
  missingPermissions: [emptyIextendedPermission],
} as IPermissionCheck;

// Singleton
export class PermissionService {
  static getInstance(): PermissionService {
    if (!PermissionService.instance) {
      PermissionService.instance = new PermissionService();
    }

    return PermissionService.instance;
  }

  // Vars
  private static instance: PermissionService;

  private static _hasRight(askedPermWithFullPath: string, allowedWithFullPath: string[]): boolean {
    askerPermLoop: for (const allowedPerm of allowedWithFullPath) {
      // split the string per '.'
      const allowedPermSplit = allowedPerm.split('.');
      const askedPermSplit = askedPermWithFullPath.split('.');

      // if the allowed perm size is bigger than the asked perm, the user doesn't have the perm
      if (allowedPermSplit.length > askedPermSplit.length) {
        continue; // don't match
      }

      // for every part of the allowedPerm, check if the part is matching in askedPerm
      for (let i = 0; i < allowedPermSplit.length; i++) {
        if (allowedPermSplit[i] !== askedPermSplit[i]) {
          continue askerPermLoop; // don't match
        }
      }

      return true; // match
    }

    return false;
  }

  private permissionTemplate: IPermissionsTemplate;
  private readonly optimizedTemplate: Map<string, IExtendedPermission>;

  // Methods
  private constructor() {
    this.optimizedTemplate = new Map<string, IExtendedPermission>();
  }

  public getTemplate(): IPermissionsTemplate {
    return this.permissionTemplate;
  }

  public setTemplate(template: IPermissionsTemplate) {
    this.permissionTemplate = template;

    this.optimizedTemplate.set(template.root.name, {
      ...template.root,
      fullPath: template.root.name,
    });

    for (const perm of template.root.permissions || []) {
      this.recursiveConvertPermissionTemplate(perm, template.root.name);
    }
  }

  /**
   * Return a list of permission which fail this test. If len of return === 0, then all perms are allowed.
   * We don't only return a boolean because you may need to display the list of missing permissions.
   * If the user is a super user, we always return an empty array (authorized for everything).
   * @param perms list of permissions the user has
   * @param user the user to check permissions for
   * @returns missing permissions
   */
  public HasRight(perms: string[], user: IUser): IExtendedPermission[] {
    if (isNullOrUndefined(user)) {
      return [emptyIextendedPermission];
    }

    if (hasRole(user, [ROLE_SUPER])) {
      return [];
    }

    if (isNullOrUndefined(this.optimizedTemplate) || isNullOrUndefined(user.PermissionRole)) {
      return [emptyIextendedPermission];
    }

    // Convert asked perms to IExtended permissions
    const permsFullPath = [] as IExtendedPermission[];
    for (const p of perms) {
      const permObj = this.optimizedTemplate.get(p);
      if (permObj !== undefined) {
        permsFullPath.push(permObj);
      } else {
        console.error('This permission doesnt exist: ', p);
        return [
          {
            name: p,
            description: 'doesnt exist',
            fullPath: '?',
            permissions: [],
          },
        ];
      }
    }

    // Convert allowed perms to full path
    const permsAllowedFullPath = [] as string[];
    for (let p of user.PermissionRole.permissions.split(',')) {
      p = p.replace(' ', '');
      const permObj = this.optimizedTemplate.get(p);
      if (permObj !== undefined) {
        permsAllowedFullPath.push(permObj.fullPath);
      } else {
        warn('This allowed permission doesnt exist: ', p);
      }
    }

    if (permsAllowedFullPath.length === 0) {
      return permsFullPath;
    }

    // Search
    const notAllowed = [] as IExtendedPermission[];
    for (const askedPerm of permsFullPath) {
      if (!PermissionService._hasRight(askedPerm.fullPath, permsAllowedFullPath)) {
        notAllowed.push(askedPerm);
      }
    }

    return notAllowed;
  }

  private recursiveConvertPermissionTemplate(currentNode: IPermission, parentName: string) {
    const out = currentNode as IExtendedPermission;

    const curNodeName = parentName + '.' + currentNode.name;

    out.fullPath = curNodeName;
    this.optimizedTemplate.set(currentNode.name, out);
    for (const perm of currentNode.permissions || []) {
      this.recursiveConvertPermissionTemplate(perm, curNodeName);
    }
  }
}

export function IS_AUTHORIZED(perms: string[], user: IUser): IPermissionCheck {
  // return allowedEmptyIPermissionCheck; // TODO Uncomment to disable client-side permission guards
  try {
    const missingPermissions = PermissionService.getInstance().HasRight(perms, user);
    return {
      isAuthorized: missingPermissions.length === 0,
      missingPermissions,
    } as IPermissionCheck;
  } catch (e) {
    console.error(e);
    return {
      isAuthorized: false,
      missingPermissions: [emptyIextendedPermission],
    } as IPermissionCheck;
  }
}

/**
 * Will accept a user's request for authorization if they have at least one required permission,
 * even if they don't have all of them.
 * @param perms list of permissions the user has
 * @param user the user to check permissions for
 * @returns missing permissions
 */
export function IS_AUTHORIZED_OR(perms: string[], user: IUser): IPermissionCheck {
  // return allowedEmptyIPermissionCheck; // TODO Uncomment to disable client-side permission guards
  try {
    const missingPerms = [] as IExtendedPermission[];
    for (const p of perms) {
      const hasRight: IExtendedPermission[] = PermissionService.getInstance().HasRight([p], user);
      if (hasRight.length === 0) {
        return allowedEmptyIPermissionCheck;
      } else {
        missingPerms.push(...hasRight);
      }
    }

    return {
      isAuthorized: missingPerms.length === 0,
      missingPermissions: missingPerms,
    } as IPermissionCheck;
  } catch (e) {
    console.error(e);
    return {
      isAuthorized: false,
      missingPermissions: [emptyIextendedPermission],
    } as IPermissionCheck;
  }
}
